import type { AppLanguage } from '../i18n/types';
import {
  bookingRecordAssetName,
  bookingRecordDateRangeLabel,
  bookingRecordIsHourly,
  bookingRecordScheduleLabel,
} from './bookingListDisplay';
import { bookingRecordBookerLabel, bookingRecordStatusDisplay } from './bookingRecordBooker';
import { bookingRecordPrimaryId } from './bookingRecordMutate';

export type BookingCalendarEventInput = {
  title: string;
  startDateIso: string;
  endDateIso: string;
  location?: string | null;
  description?: string | null;
  bookingId?: string | null;
};

function pickIso(value: unknown): string | null {
  if (typeof value !== 'string' || !value.trim()) {
    return null;
  }
  const ms = Date.parse(value.trim());
  if (Number.isNaN(ms)) {
    return null;
  }
  return new Date(ms).toISOString();
}

/** UTC stamp for Google Calendar `dates=` (YYYYMMDDTHHmmssZ). */
export function isoToGoogleCalendarUtcStamp(iso: string): string | null {
  const normalized = pickIso(iso);
  if (!normalized) {
    return null;
  }
  return normalized.replace(/[-:]/g, '').replace(/\.\d{3}Z$/, 'Z');
}

function escapeIcsText(value: string): string {
  return value
    .replace(/\\/g, '\\\\')
    .replace(/;/g, '\\;')
    .replace(/,/g, '\\,')
    .replace(/\r?\n/g, '\\n');
}

function foldIcsLine(line: string): string {
  if (line.length <= 75) {
    return line;
  }
  const parts: string[] = [];
  let rest = line;
  parts.push(rest.slice(0, 75));
  rest = rest.slice(75);
  while (rest.length > 0) {
    parts.push(` ${rest.slice(0, 74)}`);
    rest = rest.slice(74);
  }
  return parts.join('\r\n');
}

export function buildGoogleCalendarAddEventUrl(input: BookingCalendarEventInput): string | null {
  const start = isoToGoogleCalendarUtcStamp(input.startDateIso);
  const end = isoToGoogleCalendarUtcStamp(input.endDateIso);
  if (!start || !end) {
    return null;
  }
  const params = new URLSearchParams({
    action: 'TEMPLATE',
    text: input.title.trim() || 'Placy booking',
    dates: `${start}/${end}`,
  });
  if (input.location?.trim()) {
    params.set('location', input.location.trim());
  }
  if (input.description?.trim()) {
    params.set('details', input.description.trim());
  }
  return `https://calendar.google.com/calendar/render?${params.toString()}`;
}

export function buildBookingIcsContent(input: BookingCalendarEventInput): string | null {
  const start = isoToGoogleCalendarUtcStamp(input.startDateIso);
  const end = isoToGoogleCalendarUtcStamp(input.endDateIso);
  if (!start || !end) {
    return null;
  }
  const uid =
    input.bookingId?.trim() ?
      `booking-${input.bookingId.trim()}@offizz.ai`
    : `booking-${start}-${end}@offizz.ai`;
  const now = isoToGoogleCalendarUtcStamp(new Date().toISOString())!;
  const lines = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//Placy//Booking//EN',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
    'BEGIN:VEVENT',
    foldIcsLine(`UID:${uid}`),
    foldIcsLine(`DTSTAMP:${now}`),
    foldIcsLine(`DTSTART:${start}`),
    foldIcsLine(`DTEND:${end}`),
    foldIcsLine(`SUMMARY:${escapeIcsText(input.title.trim() || 'Placy booking')}`),
  ];
  if (input.location?.trim()) {
    lines.push(foldIcsLine(`LOCATION:${escapeIcsText(input.location.trim())}`));
  }
  if (input.description?.trim()) {
    lines.push(foldIcsLine(`DESCRIPTION:${escapeIcsText(input.description.trim())}`));
  }
  lines.push('END:VEVENT', 'END:VCALENDAR');
  return `${lines.join('\r\n')}\r\n`;
}

export function downloadBookingIcsFile(icsContent: string, filename: string): void {
  const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.rel = 'noopener';
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

export function bookingCalendarEventInputFromParts(parts: {
  assetName: string;
  startDateIso: unknown;
  endDateIso: unknown;
  assetAddress?: string | null;
  scheduleText?: string | null;
  bookingId?: string | null;
  peopleCount?: number | null;
  title?: string | null;
  descriptionLines?: string[] | null;
}): BookingCalendarEventInput | null {
  const startDateIso = pickIso(parts.startDateIso);
  const endDateIso = pickIso(parts.endDateIso);
  if (!startDateIso || !endDateIso) {
    return null;
  }
  if (new Date(endDateIso).getTime() <= new Date(startDateIso).getTime()) {
    return null;
  }
  const titleBase = parts.assetName.trim() || 'Placy booking';
  const descriptionLines = (
    parts.descriptionLines ??
    [
      parts.scheduleText?.trim(),
      parts.bookingId?.trim() ? `Booking ID: ${parts.bookingId.trim()}` : null,
      typeof parts.peopleCount === 'number' && Number.isFinite(parts.peopleCount)
        ? `Guests: ${parts.peopleCount}`
        : null,
    ]
  ).filter((line): line is string => Boolean(line?.trim()));
  return {
    title: parts.title?.trim() || `${titleBase} — Placy`,
    startDateIso,
    endDateIso,
    location: parts.assetAddress?.trim() || null,
    description: descriptionLines.length > 0 ? descriptionLines.join('\n') : null,
    bookingId: parts.bookingId?.trim() || null,
  };
}

export function safeBookingIcsFilename(bookingId: string | null | undefined): string {
  const slug = (bookingId?.trim() || 'booking').replace(/[^\w.-]+/g, '-');
  return `offizz-${slug}.ics`;
}

/** Build a calendar event from an existing booking row (e.g. owner schedule). */
export function bookingCalendarEventFromRecord(
  booking: Record<string, unknown>,
  options?: {
    assetName?: string | null;
    assetAddress?: string | null;
    language?: AppLanguage;
  },
): BookingCalendarEventInput | null {
  const language = options?.language ?? 'en';
  const assetName = options?.assetName?.trim() || bookingRecordAssetName(booking);
  const booker = bookingRecordBookerLabel(booking);
  const scheduleText = bookingRecordIsHourly(booking)
    ? bookingRecordScheduleLabel(booking, language)
    : bookingRecordDateRangeLabel(booking, language);
  const bookingId = bookingRecordPrimaryId(booking);
  const status = bookingRecordStatusDisplay(booking);
  const people =
    typeof booking.amount_of_people === 'number' && Number.isFinite(booking.amount_of_people)
      ? booking.amount_of_people
      : null;

  const descriptionLines = [
    booker ? `Guest: ${booker}` : null,
    scheduleText?.trim() || null,
    status ? `Status: ${status}` : null,
    bookingId ? `Booking ID: ${bookingId}` : null,
    people !== null ? `Guests: ${people}` : null,
  ].filter((line): line is string => Boolean(line?.trim()));

  const title = booker ? `${assetName} — ${booker}` : `${assetName} — Placy`;

  return bookingCalendarEventInputFromParts({
    assetName,
    startDateIso: booking.start_date,
    endDateIso: booking.end_date,
    assetAddress: options?.assetAddress,
    bookingId,
    title,
    descriptionLines,
  });
}
